<template>
  <div class="json-viewer">
    <div class="json-toolbar">
      <div class="json-info">
        <span class="json-size">{{ dataSize }}</span>
        <span class="json-type">Content Preview</span>
      </div>
      <div class="json-actions">
        <button @click="toggleView" class="action-btn" :class="{ active: viewMode === 'preview' }">
          Preview
        </button>
        <button @click="toggleView" class="action-btn" :class="{ active: viewMode === 'raw' }">
          Raw JSON
        </button>
        <button @click="copyToClipboard" class="action-btn">
          {{ copied ? 'Copied!' : 'Copy' }}
        </button>
      </div>
    </div>
    
    <div class="json-content">
      <!-- Preview Mode - Readable Content -->
      <div v-if="viewMode === 'preview'" class="preview-content">
        <!-- Project Header -->
        <div v-if="data.project_name" class="project-header">
          <h1 class="project-title">{{ data.project_name }}</h1>
        </div>

        <!-- Content Sections -->
        <div v-for="(section, key) in contentSections" :key="key" class="content-section">
          <div class="section-header">
            <h2>{{ formatSectionTitle(key) }}</h2>
          </div>
          
          <div class="section-body">
            <!-- Header & Sub-header -->
            <div v-if="section.header" class="content-item header-item">
              <h3 class="content-header">{{ section.header }}</h3>
              <p v-if="section['sub-header']" class="content-subheader">{{ section['sub-header'] }}</p>
            </div>

            <!-- Description -->
            <div v-if="section.description" class="content-item description-item">
              <div class="description-label">Description</div>
              <p class="description-text">{{ section.description }}</p>
            </div>

            <!-- Developer & Architect Descriptions -->
            <div v-if="section['developer-description']" class="content-item developer-item">
              <div class="developer-label">Developer Information</div>
              <p class="developer-text">{{ section['developer-description'] }}</p>
            </div>

            <div v-if="section['architect-description']" class="content-item architect-item">
              <div class="architect-label">Architect Information</div>
              <p class="architect-text">{{ section['architect-description'] }}</p>
            </div>

            <!-- Features List -->
            <div v-if="section.features && Array.isArray(section.features)" class="content-item features-item">
              <div class="features-label">Key Features</div>
              <ul class="features-list">
                <li v-for="(feature, index) in section.features" :key="index" class="feature-item">
                  {{ feature }}
                </li>
              </ul>
            </div>

            <!-- Images & Media -->
            <div v-if="section['video-poster'] || section.img" class="content-item media-item">
              <div class="media-label">Media</div>
              <div class="media-links">
                <div v-if="section['video-poster']" class="media-link">
                  <span class="media-type">Video Poster:</span>
                  <a :href="section['video-poster']" target="_blank" class="media-url">
                    {{ formatUrl(section['video-poster']) }}
                  </a>
                </div>
                <div v-if="section.img" class="media-link">
                  <span class="media-type">Image:</span>
                  <a :href="section.img" target="_blank" class="media-url">
                    {{ formatUrl(section.img) }}
                  </a>
                </div>
              </div>
            </div>

            <!-- Call-to-Action Buttons -->
            <div v-if="hasButtons(section)" class="content-item buttons-item">
              <div class="buttons-label">Call-to-Action</div>
              <div class="buttons-list">
                <div v-if="section['cat/button']" class="button-item primary">
                  <span class="button-label">Primary:</span>
                  <span class="button-text">{{ section['cat/button'] }}</span>
                </div>
                <div v-if="section['cat/button/secondary']" class="button-item secondary">
                  <span class="button-label">Secondary:</span>
                  <span class="button-text">{{ section['cat/button/secondary'] }}</span>
                </div>
                <div v-if="section['cat/button2']" class="button-item tertiary">
                  <span class="button-label">Additional:</span>
                  <span class="button-text">{{ section['cat/button2'] }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- User Journey Steps -->
        <div v-if="Object.keys(stepSections).length > 0" class="steps-container">
          <div class="steps-header">
            <h2>🚀 User Journey & Form Steps</h2>
            <p>Interactive elements and user flow</p>
          </div>
          
          <div v-for="(step, key) in stepSections" :key="'step-' + key" class="step-section">
            <div class="step-header">
              <h3>{{ formatStepTitle(key) }}</h3>
            </div>
            
            <div class="step-body">
              <!-- Regular Steps (step1-7) -->
              <div v-if="key.startsWith('step')" class="step-content">
                <!-- Step paragraphs -->
                <div v-if="step['p-1']" class="step-paragraph">
                  <p class="step-text primary">{{ step['p-1'] }}</p>
                </div>
                <div v-if="step['p-2']" class="step-paragraph">
                  <p class="step-text secondary">{{ step['p-2'] }}</p>
                </div>
                <div v-if="step.p && !step['p-1']" class="step-paragraph">
                  <p class="step-text primary">{{ step.p }}</p>
                </div>

                <!-- Input fields -->
                <div v-if="step.label" class="step-input">
                  <div class="input-label">{{ step.label }}</div>
                  <div v-if="step.placeholder" class="input-placeholder">
                    Placeholder: {{ step.placeholder }}
                  </div>
                </div>

                <!-- Choice options -->
                <div v-if="step.yes || step.no" class="step-choices">
                  <div class="choice-label">User Options:</div>
                  <div v-if="step.yes" class="choice-option positive">
                    <span class="choice-type">✓ Yes:</span>
                    <span class="choice-text">{{ step.yes }}</span>
                  </div>
                  <div v-if="step.no" class="choice-option negative">
                    <span class="choice-type">✗ No:</span>
                    <span class="choice-text">{{ step.no }}</span>
                  </div>
                </div>

                <!-- Bedroom options -->
                <div v-if="step.bedrooms && Array.isArray(step.bedrooms)" class="step-bedrooms">
                  <div class="bedrooms-label">Available Options:</div>
                  <div class="bedrooms-list">
                    <div v-for="(bedroom, index) in step.bedrooms" :key="index" class="bedroom-option">
                      {{ bedroom }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Success/Failure Messages -->
              <div v-else-if="key === 'success'" class="success-content">
                <div class="success-icon">✅</div>
                <p class="success-text">{{ step.p }}</p>
              </div>

              <div v-else-if="key === 'failure'" class="failure-content">
                <div class="failure-icon">❌</div>
                <p class="failure-text">{{ step }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Raw JSON Mode -->
      <div v-else class="raw-content">
        <pre class="json-formatted" v-html="formattedJson"></pre>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JsonViewer',
  props: {
    data: {
      type: [Object, Array, String, Number, Boolean],
      required: true
    }
  },
  data() {
    return {
      viewMode: 'preview',
      copied: false
    }
  },
  computed: {
    dataSize() {
      const jsonString = JSON.stringify(this.data)
      const bytes = new Blob([jsonString]).size
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    
    contentSections() {
      // Filter out non-section data and return only the content sections
      const sections = {}
      Object.keys(this.data).forEach(key => {
        if (key.startsWith('section') && typeof this.data[key] === 'object') {
          sections[key] = this.data[key]
        }
      })
      return sections
    },
    
    stepSections() {
      // Get all step-related content
      const steps = {}
      Object.keys(this.data).forEach(key => {
        if ((key.startsWith('step') || key === 'success' || key === 'failure') && this.data[key]) {
          steps[key] = this.data[key]
        }
      })
      return steps
    },
    
    formattedJson() {
      return this.syntaxHighlight(JSON.stringify(this.data, null, 2))
    }
  },
  methods: {
    toggleView() {
      this.viewMode = this.viewMode === 'preview' ? 'raw' : 'preview'
    },
    
    formatSectionTitle(key) {
      // Convert "section1" to "Section 1"
      return key.replace(/section(\d+)/i, 'Section $1')
    },
    
    formatUrl(url) {
      // Shorten long URLs for display
      if (url.length > 60) {
        return url.substring(0, 40) + '...' + url.substring(url.length - 15)
      }
      return url
    },
    
    hasButtons(section) {
      return section['cat/button'] || section['cat/button/secondary'] || section['cat/button2']
    },
    
    formatStepTitle(key) {
      if (key === 'success') return 'Success Message'
      if (key === 'failure') return 'Error Message'
      return key.replace(/step(\d+)/i, 'Step $1')
    },
    
    syntaxHighlight(json) {
      if (typeof json !== 'string') {
        json = JSON.stringify(json, undefined, 2)
      }
      
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      
      return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
        let cls = 'json-number'
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key'
          } else {
            cls = 'json-string'
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean'
        } else if (/null/.test(match)) {
          cls = 'json-null'
        }
        return '<span class="' + cls + '">' + match + '</span>'
      })
    },
    
    async copyToClipboard() {
      try {
        const jsonString = JSON.stringify(this.data, null, 2)
        await navigator.clipboard.writeText(jsonString)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        const textArea = document.createElement('textarea')
        textArea.value = JSON.stringify(this.data, null, 2)
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      }
    }
  }
}
</script>

<style scoped>
.json-viewer {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #e1e5e9;
}

.json-toolbar {
  background: #f6f8fa;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e1e5e9;
}

.json-info {
  display: flex;
  gap: 12px;
  align-items: center;
}

.json-size {
  color: #656d76;
  font-size: 12px;
  font-weight: 500;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.json-type {
  color: #24292f;
  font-size: 14px;
  font-weight: 600;
}

.json-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: white;
  color: #24292f;
  border: 1px solid #d0d7de;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: #f3f4f6;
  border-color: #8c959f;
}

.action-btn.active {
  background: #0969da;
  color: white;
  border-color: #0969da;
}

.json-content {
  max-height: 700px;
  overflow-y: auto;
}

/* Project Header */
.project-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  text-align: center;
}

.project-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
}

/* Content Sections */
.content-section {
  border-bottom: 1px solid #f0f0f0;
}

.content-section:last-child {
  border-bottom: none;
}

.section-header {
  background: #f8f9fa;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
}

.section-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #495057;
}

.section-body {
  padding: 20px 24px;
}

/* Content Items */
.content-item {
  margin-bottom: 24px;
}

.content-item:last-child {
  margin-bottom: 0;
}

/* Headers */
.header-item .content-header {
  color: #2c3e50;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.header-item .content-subheader {
  color: #6c757d;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.4;
}

/* Description */
.description-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.description-text {
  color: #343a40;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}

/* Developer & Architect Information */
.developer-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.developer-text {
  color: #343a40;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  background: #e8f4fd;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.architect-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.architect-text {
  color: #343a40;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  background: #f3e5f5;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #9c27b0;
}

/* Features */
.features-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.features-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.feature-item {
  background: #f8f9fa;
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 6px;
  border-left: 4px solid #28a745;
  color: #343a40;
  font-size: 14px;
  line-height: 1.5;
}

.feature-item:last-child {
  margin-bottom: 0;
}

/* Media */
.media-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.media-links {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.media-type {
  font-weight: 600;
  color: #6c757d;
  min-width: 100px;
  font-size: 13px;
}

.media-url {
  color: #0969da;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
}

.media-url:hover {
  text-decoration: underline;
}

/* Buttons */
.buttons-label {
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.buttons-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.button-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 6px;
}

.button-item.primary {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
}

.button-item.secondary {
  background: #f3e5f5;
  border-left: 4px solid #9c27b0;
}

.button-item.tertiary {
  background: #e8f5e8;
  border-left: 4px solid #4caf50;
}

.button-label {
  font-weight: 600;
  color: #6c757d;
  min-width: 80px;
  font-size: 13px;
}

.button-text {
  color: #343a40;
  font-size: 14px;
  font-weight: 500;
}

/* Raw JSON Styles */
.raw-content {
  padding: 20px;
  background: #f6f8fa;
}

.json-formatted {
  margin: 0;
  color: #24292f;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.json-formatted :deep(.json-key) {
  color: #0969da;
  font-weight: 600;
}

.json-formatted :deep(.json-string) {
  color: #0a3069;
}

.json-formatted :deep(.json-number) {
  color: #8250df;
}

.json-formatted :deep(.json-boolean) {
  color: #cf222e;
  font-weight: 600;
}

.json-formatted :deep(.json-null) {
  color: #656d76;
  font-style: italic;
}

/* Steps Container */
.steps-container {
  margin-top: 20px;
  border-top: 2px solid #e9ecef;
}

.steps-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 20px 24px;
  text-align: center;
}

.steps-header h2 {
  margin: 0 0 5px 0;
  font-size: 20px;
  font-weight: 700;
}

.steps-header p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.step-section {
  border-bottom: 1px solid #f0f0f0;
}

.step-section:last-child {
  border-bottom: none;
}

.step-header {
  background: #e8f5e8;
  padding: 12px 24px;
  border-bottom: 1px solid #d4edda;
}

.step-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #155724;
}

.step-body {
  padding: 20px 24px;
}

/* Step Content */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-paragraph {
  margin-bottom: 12px;
}

.step-text {
  margin: 0;
  line-height: 1.6;
}

.step-text.primary {
  color: #2c3e50;
  font-size: 15px;
  font-weight: 500;
}

.step-text.secondary {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
}

/* Input Fields */
.step-input {
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 6px;
  border-left: 4px solid #007bff;
}

.input-label {
  font-weight: 600;
  color: #007bff;
  font-size: 14px;
  margin-bottom: 4px;
}

.input-placeholder {
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
}

/* Choice Options */
.step-choices {
  background: #fff3cd;
  padding: 16px;
  border-radius: 6px;
  border-left: 4px solid #ffc107;
}

.choice-label {
  font-weight: 600;
  color: #856404;
  font-size: 14px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.choice-option {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px 12px;
  border-radius: 4px;
}

.choice-option:last-child {
  margin-bottom: 0;
}

.choice-option.positive {
  background: #d4edda;
  border-left: 3px solid #28a745;
}

.choice-option.negative {
  background: #f8d7da;
  border-left: 3px solid #dc3545;
}

.choice-type {
  font-weight: 600;
  min-width: 40px;
}

.choice-option.positive .choice-type {
  color: #155724;
}

.choice-option.negative .choice-type {
  color: #721c24;
}

.choice-text {
  color: #495057;
  font-size: 14px;
}

/* Bedroom Options */
.step-bedrooms {
  background: #e3f2fd;
  padding: 16px;
  border-radius: 6px;
  border-left: 4px solid #2196f3;
}

.bedrooms-label {
  font-weight: 600;
  color: #1565c0;
  font-size: 14px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bedrooms-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.bedroom-option {
  background: white;
  padding: 8px 12px;
  border-radius: 4px;
  color: #1565c0;
  font-weight: 500;
  text-align: center;
  border: 1px solid #bbdefb;
}

/* Success/Failure Messages */
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  background: #d4edda;
  border-radius: 8px;
  border: 2px solid #28a745;
}

.success-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.success-text {
  color: #155724;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}

.failure-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  background: #f8d7da;
  border-radius: 8px;
  border: 2px solid #dc3545;
}

.failure-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.failure-text {
  color: #721c24;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
}
  .json-toolbar {
    flex-direction: column;
    gap: 12px;
  }
  
  .json-actions {
    width: 100%;
    justify-content: center;
  }
  
  .action-btn {
    flex: 1;
  }
  
  .section-body {
    padding: 16px 20px;
  }
  
  .project-header {
    padding: 20px;
  }
  
  .project-title {
    font-size: 24px;
  }
  
  .media-link {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .button-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

</style> 